import serverName from "Config/config";
import WrapperAPI from "Utils/WrapperAPI";

const useLogin = async (
    username,
    password,
    withUnixi = false,
    userCode = null,
    rememberMe = false
) => {
    return await WrapperAPI(
        async () => {
            return await fetch(`${serverName}api/authentication/login`, {
                method: "POST",
                body: JSON.stringify({
                    username: username,
                    password: password,
                    with_unixi: withUnixi,
                    user_code: userCode,
                    remember_me: rememberMe,
                }),
                credentials: "include",
            });
        },
        {
            ignore_401: true,
        }
    );
};

/**
 * Resolve an address to its sign-in method. Three outcomes matter to the caller:
 * a `redirect_url` (SAML, go to the IdP), `password_login: true` (show the
 * password field), or a 400 carrying `password_login: false` - SAML *is*
 * configured for the tenant and could not be initiated.
 *
 * Deliberately not on WrapperAPI, for the same reason as Services/BreakGlassAPI:
 * that wrapper toasts "Server Error Occurred - Status Code: 400" and throws on
 * any status >= 400, so it returned `undefined` for that last case and the login
 * page could not tell a broken IdP apart from a malformed address. That status
 * is the signal "IdP not working?" is shown on, so it has to survive the
 * transport.
 *
 * No Content-Type header, unlike BreakGlassAPI: `serverName` is a
 * runtime-injected origin that need not match the page's, and adding one would
 * turn this simple request into a preflighted one on the main login path.
 * login_sso reads the raw body with json.loads and never inspects the type.
 *
 * Never rejects - a transport failure comes back in the same shape with
 * httpStatus 0, so Continue always re-enables its button.
 */
const onContinue = async (username) => {
    let response;
    try {
        response = await fetch(`${serverName}api/authentication/login_sso`, {
            method: "POST",
            body: JSON.stringify({
                email: username,
            }),
            credentials: "include",
        });
    } catch (error) {
        return {
            status: false,
            httpStatus: 0,
            message: "Could not reach the server. Check your connection and try again.",
        };
    }

    let payload = {};
    try {
        payload = await response.json();
    } catch (error) {
        payload = {};
    }

    return { ...payload, httpStatus: response.status };
};

const useLoginMagicLink = async (username) => {
    return await WrapperAPI(
        async () => {
            return await fetch(
                `${serverName}api/authentication/send_magic_link`,
                {
                    method: "POST",
                    body: JSON.stringify({
                        user_email: username,
                    }),
                    credentials: "include",
                }
            );
        },
        {
            ignore_401: true,
        }
    );
};

const useLoginMagicLinkConfirm = async (magicLink) => {
    return await WrapperAPI(
        async () => {
            return await fetch(
                `${serverName}api/authentication/login_magic_link?magicLink=${magicLink}`,
                {
                    method: "GET",
                    credentials: "include",
                }
            );
        },
        {
            ignore_401: true,
        }
    );
};

const UseMfaLogin = async (code) => {
    return await WrapperAPI(
        async () => {
            return await fetch(`${serverName}api/authentication/login_mfa`, {
                method: "POST",
                body: JSON.stringify({ token: code }),
                credentials: "include",
            });
        },
        {
            ignore_401: true,
        }
    );
};

const useResetPassword = async (username) => {
    return await WrapperAPI(
        async () => {
            return await fetch(
                `${serverName}api/authentication/reset_password`,
                {
                    method: "POST",
                    body: JSON.stringify({
                        email: username,
                        new: true,
                    }),
                    credentials: "include",
                }
            );
        },
        {
            ignore_401: true,
        }
    );
};

const UseSetPassword = async (token, password) => {
    return await WrapperAPI(
        async () => {
            return await fetch(`${serverName}api/authentication/set_password`, {
                method: "POST",
                body: JSON.stringify({ token: token, password: password }),
                credentials: "include",
            });
        },
        {
            ignore_401: true,
        }
    );
};

const useLoginMicrosoft = async (credential) => {
    return await WrapperAPI(
        async () => {
            return await fetch(`${serverName}api/authentication/login`, {
                method: "POST",
                body: JSON.stringify({
                    type: "microsoft",
                    credential: credential,
                }),
                credentials: "include",
            });
        },
        {
            ignore_401: true,
        }
    );
};

const useLoginGoogle = async (credential) => {
    return await WrapperAPI(
        async () => {
            return await fetch(`${serverName}api/authentication/login`, {
                method: "POST",
                body: JSON.stringify({
                    type: "google",
                    credential: credential,
                }),
                credentials: "include",
            });
        },
        {
            ignore_401: true,
        }
    );
};

const getProfileData = async () => {
    return await WrapperAPI(async () => {
        return await fetch(`${serverName}api/get_profile_data`, {
            method: "GET",
            credentials: "include",
        });
    });
};

/**
 * Consumer (end-user) Google OAuth login.
 * Sends the Google credential to the backend which validates it,
 * resolves the tenant, and returns a SAML assertion to be POSTed
 * to the SAML login endpoint.
 *
 * Expected response:
 *   { status: true, saml_url: string, saml_response: string, relay_state?: string }
 *   OR { status: true, redirect_url: string }
 */
const consumerGoogleLogin = async (credential) => {
    return await WrapperAPI(
        async () => {
            return await fetch(
                `${serverName}api/authentication/consumer_google_login`,
                {
                    method: "POST",
                    body: JSON.stringify({
                        type: "google",
                        credential: credential,
                    }),
                    credentials: "include",
                }
            );
        },
        {
            ignore_401: true,
        }
    );
};

export {
    onContinue,
    useLogin,
    useLoginMagicLink,
    useLoginMagicLinkConfirm,
    UseMfaLogin,
    useResetPassword,
    UseSetPassword,
    useLoginGoogle,
    useLoginMicrosoft,
    getProfileData,
    consumerGoogleLogin,
};
